import React, { memo, useState, useCallback, useEffect, useMemo } from "react";
import Link from "next/link";
import NextImage from "next/image"; // Renamed to avoid conflict
import PropTypes from "prop-types";
import { useInView } from "react-intersection-observer";
// Feather icons
import { 
  FiHome, 
  FiSquare, 
  FiMapPin, 
  FiClock, 
  FiAlertTriangle, 
  FiImage 
} from "react-icons/fi";
import { FaBed, FaBath, FaChevronLeft, FaChevronRight } from "react-icons/fa";

// Cache status constants
const STATUS_STYLES = Object.freeze({
  for_sale: "bg-green-500",
  sold: "bg-red-900",
  under_contract: "bg-yellow-500",
  pending: "bg-orange-500",
  off_market: "bg-gray-500",
});

const STATUS_TEXT = Object.freeze({
  active: "Active",
  for_sale: "For Sale",
  sold: "Sold",
  under_contract: "Under Contract",
  pending: "Pending",
  off_market: "Off Market",
});

// Static fallback image
const FALLBACK_IMAGE_DATA = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100' viewBox='0 0 100 100'%3E%3Crect width='100' height='100' fill='%23f1f5f9'/%3E%3Cpath d='M30,40 L70,40 L70,70 L30,70 Z' fill='%23e2e8f0'/%3E%3Cpath d='M50,20 L80,50 L80,80 L20,80 L20,50 Z' fill='%23cbd5e1'/%3E%3C/svg%3E";

// Global image error tracking
const problematicImages = new Set();

// Image URL utility
const getImageUrl = (imagePath, fallbackIndex = 0) => {
  if (typeof imagePath !== "string" || !imagePath.trim()) return null;

  if (problematicImages.has(imagePath) && fallbackIndex === 0) {
    return imagePath;
  }

  const baseUrl = process.env.NEXT_PUBLIC_BLOB_BASE_URL?.trim();
  if (!baseUrl) return imagePath;

  try {
    if (imagePath.startsWith('http')) {
      return imagePath;
    }
    
    const encodedPath = encodeURIComponent(imagePath.trim());
    const cacheBuster = fallbackIndex > 0 ? `&_cb=${Date.now()}-${fallbackIndex}` : '';
    
    const params = new URLSearchParams({
      w: '800',
      h: '600',
      q: fallbackIndex > 0 ? '65' : '80',
      f: 'webp'
    });
    
    return `${baseUrl}/${encodedPath}?${params.toString()}${cacheBuster}`;
  } catch (err) {
    return imagePath;
  }
};

const getFallbackImageUrl = (images = [], currentIndex = 0) => {
  for (let i = currentIndex + 1; i < images.length; i++) {
    if (images[i]) {
      return { url: images[i], index: i };
    }
  }
  return { url: null, index: -1 };
};

// Safe icon rendering component
const SafeIcon = ({ icon: Icon, ...props }) => {
  if (!Icon) return null;
  try {
    return <Icon {...props} />;
  } catch (e) {
    return null;
  }
};

const ListingCard = memo(function ListingCard({ listing, onClick, preloadImages = false, size = "default" }) {
  const [imageError, setImageError] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(-1);
  const [retryAttempt, setRetryAttempt] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [hasInitialized, setHasInitialized] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
    rootMargin: "200px 0px",
    threshold: 0.1,
  });

  // Initialize image index
  useEffect(() => {
    if (Array.isArray(listing?.images) && listing.images.length > 0) {
      setCurrentImageIndex(0);
    } else {
      setCurrentImageIndex(-1);
    }
  }, [listing]);

  if (!listing) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 text-gray-500 text-center flex flex-col items-center justify-center min-h-[200px]">
        <SafeIcon icon={FiAlertTriangle} size={24} className="mb-2 text-amber-400" />
        <p>Property listing unavailable</p>
      </div>
    );
  }

  if (!listing._id) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-4 text-gray-500 text-center flex flex-col items-center justify-center min-h-[200px]">
        <SafeIcon icon={FiAlertTriangle} size={24} className="mb-2 text-amber-400" />
        <p>Invalid listing data</p>
      </div>
    );
  }

  const {
    _id,
    title = "Untitled Property",
    price = 0,
    city: directCity,
    state: directState,
    location,
    address,
    bedrooms = 0,
    bathrooms = 0,
    squareFeet = 0,
    images = [],
    status = "for_sale",
    listingType = "sale",
    createdAt,
    propertyType = "residential",
  } = listing;

  // Handle direct city/state, nested location fields, and string location with multiple fallbacks
  const locObj = (typeof location === 'object' && location) ? location : null;
  const locationString = typeof location === 'string' && location.trim() ? location.trim() : null;
  const pickFirst = (...vals) => vals.find(v => typeof v === 'string' && v.trim())?.trim() || null;
  const city = pickFirst(locObj?.city, locObj?.town, locObj?.locality, locObj?.area, locObj?.neighborhood, locObj?.lga, locObj?.district, directCity);
  const state = pickFirst(locObj?.state, locObj?.region, locObj?.province, directState);
  const nestedAddress = pickFirst(locObj?.address, locObj?.street, locObj?.streetAddress, locObj?.formattedAddress, locObj?.full, locObj?.name);

  // Determine status label and color
  let statusLabel = "";
  let statusColor = "bg-gray-500";

  // Normalize listingType to lowercase string for consistent comparison
  const normalizedListingType = typeof listingType === "string" ? listingType.toLowerCase() : "";

  if (status === "sold") {
    statusLabel = "Sold";
    statusColor = "bg-red-900";
  } else if (["rent", "lease", "shortlet"].includes(normalizedListingType) && status === "taken") {
    statusLabel = "Taken";
    statusColor = "bg-red-900";
  } else if (normalizedListingType === "rent") {
    statusLabel = "For Rent";
    statusColor = "bg-blue-500";
  } else if (normalizedListingType === "lease") {
    statusLabel = "For Lease";
    statusColor = "bg-yellow-500";
  } else if (normalizedListingType === "shortlet") {
    statusLabel = "Shortlet";
    statusColor = "bg-purple-500";
  } else if (["sale", "for_sale"].includes(normalizedListingType)) {
    statusLabel = "For Sale";
    statusColor = "bg-green-500";
  } else {
    statusLabel = status.charAt(0).toUpperCase() + status.slice(1);
    statusColor = STATUS_STYLES[status] || "bg-gray-500";
  }


  // Preload images - client-side only
  useEffect(() => {
    if (typeof window === 'undefined') return;
    
    if (!hasInitialized) {
      setHasInitialized(true);
      
      if (preloadImages && Array.isArray(images) && images.length > 0) {
        const imagesToPreload = images.slice(0, 3);
        imagesToPreload.forEach((imgPath) => {
          if (imgPath) {
            // Use native browser Image constructor explicitly
            const img = new window.Image();
            img.src = getImageUrl(imgPath);
            img.onerror = () => problematicImages.add(imgPath);
          }
        });
      }
    }
  }, [images, preloadImages, hasInitialized]);

  // Reset image state
  useEffect(() => {
    setImageError(false);
    setRetryAttempt(0);
    setIsLoading(true);
  }, [_id]);

  const handleClick = useCallback(() => {
    onClick?.({ listingId: _id, title });
  }, [_id, title, onClick]);

  const formattedPrice = new Intl.NumberFormat("en-NG", {
    style: "currency",
    currency: "NGN",
    maximumFractionDigits: 0,
  }).format(price);

  const locationText = locationString
    || (city && state ? `${city}, ${state}` : (city || state))
    || nestedAddress
    || (typeof address === 'string' && address.trim() ? address.trim() : null)
    || "Location not specified";

  const hasValidImages = Array.isArray(images) && images.length > 0;
  // Expect images as array of objects with url property
  const currentImageObj = hasValidImages && currentImageIndex >= 0 && currentImageIndex < images.length 
    ? images[currentImageIndex] 
    : null;
  const imageUrl = !imageError && currentImageObj && currentImageObj.url
    ? getImageUrl(currentImageObj.url, retryAttempt)
    : null;

  const isRecentListing = useMemo(() => {
    if (!createdAt) return false;
    try {
      const listingDate = new Date(createdAt);
      if (isNaN(listingDate.getTime())) return false;
      
      const currentDate = new Date();
      const differenceInDays = Math.floor((currentDate - listingDate) / (1000 * 60 * 60 * 24));
      return differenceInDays <= 7;
    } catch {
      return false;
    }
  }, [createdAt]);

  // Show 'New' flag based on listingType and createdAt
  const showNewFlag = useMemo(() => {
    if (!createdAt) return false;
    try {
      const listingDate = new Date(createdAt);
      if (isNaN(listingDate.getTime())) return false;
      const currentDate = new Date();
      let daysLimit = 7;
      if (["rent", "shortlet"].includes(listingType)) daysLimit = 3;
      else if (listingType === "lease") daysLimit = 7;
      else if (["sale", "for_sale"].includes(listingType)) daysLimit = 7;
      const differenceInDays = Math.floor((currentDate - listingDate) / (1000 * 60 * 60 * 24));
      return differenceInDays <= daysLimit;
    } catch {
      return false;
    }
  }, [createdAt, listingType]);

  // Featured badge logic (respect both boolean field or truthy string)
  const isFeatured = Boolean(listing.isFeatured);

  const PropertyIcon = useMemo(() => {
    switch (propertyType?.toLowerCase()) {
      case 'commercial': 
        return <SafeIcon icon={FiSquare} size={48} className="text-gray-400" />;
      case 'land':
        return <SafeIcon icon={FiMapPin} size={48} className="text-gray-400" />;
      default:
        return <SafeIcon icon={FiHome} size={48} className="text-gray-400" />;
    }
  }, [propertyType]);

  const handleImageLoad = () => setIsLoading(false);

  const handleImageError = () => {
    if (currentImageObj) problematicImages.add(currentImageObj.url);
    
    if (retryAttempt >= 2) {
      const { index } = getFallbackImageUrl(images, currentImageIndex);
      if (index >= 0) {
        setCurrentImageIndex(index);
        setRetryAttempt(0);
        setIsLoading(true);
      } else {
        setImageError(true);
        setIsLoading(false);
      }
    } else {
      setRetryAttempt(prev => prev + 1);
      setIsLoading(true);
    }
  };

  // Add slider navigation handlers
  const goToPrevImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev > 0 ? prev - 1 : images.length - 1
    );
    setRetryAttempt(0);
    setImageError(false);
    setIsLoading(true);
  };
  const goToNextImage = (e) => {
    e.stopPropagation();
    setCurrentImageIndex((prev) =>
      prev < images.length - 1 ? prev + 1 : 0
    );
    setRetryAttempt(0);
    setImageError(false);
    setIsLoading(true);
  };

  const imageHeight = {
    small: "h-36",
    large: "h-56", 
    default: "h-48"
  }[size];
  
  const cardStyles = {
    small: "text-sm",
    large: "", 
    default: ""
  }[size];

  return (
    <Link href={`/listings/${_id}`} passHref>
      <article
        ref={ref}
        className={`block h-full bg-white rounded-lg shadow-sm overflow-hidden transition-shadow hover:shadow-md focus-within:ring-2 focus-within:ring-blue-500 ${cardStyles}`}
        itemScope
        itemType="http://schema.org/Residence"
        onClick={handleClick}
      >
        <div className={`relative ${imageHeight} bg-gray-100 overflow-hidden`}>
          {(imageUrl && inView) ? (
            <>
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
                  <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
                </div>
              )}
              {/* Slider arrows */}
              {images.length > 1 && (
                <>
                  <button
                    type="button"
                    aria-label="Previous image"
                    className="absolute left-2 top-1/2 z-10 -translate-y-1/2 bg-white/70 hover:bg-white rounded-full p-1 shadow"
                    onClick={goToPrevImage}
                  >
                    <FaChevronLeft size={20} />
                  </button>
                  <button
                    type="button"
                    aria-label="Next image"
                    className="absolute right-2 top-1/2 z-10 -translate-y-1/2 bg-white/70 hover:bg-white rounded-full p-1 shadow"
                    onClick={goToNextImage}
                  >
                    <FaChevronRight size={20} />
                  </button>
                </>
              )}
              <NextImage
                src={imageUrl || FALLBACK_IMAGE_DATA}
                alt={title}
                fill
                sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                className={`object-cover transition-transform hover:scale-105 image-protected ${isLoading ? 'opacity-0' : 'opacity-100'}`}
                onError={handleImageError}
                onLoad={handleImageLoad}
                loading="lazy"
                quality={75}
                unoptimized={imageUrl && imageUrl.startsWith('http') && !imageUrl.includes(window?.location?.host)}
                draggable={false}
                onContextMenu={(e) => e.preventDefault()}
              />
              
              {/* CSS Watermark Overlay */}
              <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                <div
                  className="text-white font-bold opacity-40 select-none"
                  style={{
                    textShadow: "2px 2px 4px rgba(0,0,0,0.7)",
                    fontSize: "clamp(0.6rem, 2vw, 1rem)",
                  }}
                >
                  TOPDIAL.NG
                </div>
              </div>
              
              {/* Corner watermark */}
              <div className="absolute top-1 right-1 pointer-events-none">
                <div
                  className="text-white text-xs opacity-40 select-none"
                  style={{
                    textShadow: "1px 1px 2px rgba(0,0,0,0.7)",
                    fontSize: "0.6rem",
                  }}
                >
                  TOPDIAL.NG
                </div>
              </div>
              
              {/* Dots */}
              {images.length > 1 && (
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1 z-10">
                  {images.map((_, idx) => (
                    <button
                      key={idx}
                      type="button"
                      aria-label={`Go to image ${idx + 1}`}
                      className={`w-2 h-2 rounded-full ${idx === currentImageIndex ? 'bg-blue-500' : 'bg-white/70 border border-blue-200'}`}
                      onClick={e => { e.stopPropagation(); setCurrentImageIndex(idx); setRetryAttempt(0); setImageError(false); setIsLoading(true); }}
                    />
                  ))}
                </div>
              )}
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-full bg-gray-100">
              {imageError ? (
                <>
                  <SafeIcon icon={FiImage} size={32} className="text-gray-400 mb-2" />
                  <span className="text-xs text-gray-400">No image available</span>
                </>
              ) : (
                <>{PropertyIcon}</>
              )}
            </div>
          )}
          <div className="absolute bottom-0 left-0 bg-wine text-white px-3 py-1 font-medium">
            {formattedPrice}
          </div>
          {statusLabel && (
            <div className={`absolute top-2 right-2 ${["Sold","Taken"].includes(statusLabel) ? "bg-red-900" : "bg-wine"} text-white px-2 py-1 rounded text-sm`}>
              {statusLabel}
            </div>
          )}
          
          {(showNewFlag || isFeatured) && (
            <div className="absolute top-2 left-2 flex flex-col items-start gap-1 z-20">
              {isFeatured && (
                <div className="bg-amber-500 text-white px-2 py-1 rounded text-[10px] font-semibold shadow-sm tracking-wide uppercase">
                  Featured
                </div>
              )}
              {showNewFlag && (
                <div className="bg-wine text-white px-2 py-1 rounded-full text-[10px] font-medium flex items-center shadow-sm">
                  <SafeIcon icon={FiClock} size={12} className="mr-1" />
                  New
                </div>
              )}
            </div>
          )}
        </div>

        <div className="p-4">
          <h3 className="font-medium text-lg text-gray-900 line-clamp-1">
            {title}
          </h3>
          <div className="flex items-center text-gray-600 text-sm mb-3 mt-2">
            <SafeIcon icon={FiMapPin} size={14} className="mr-1 flex-shrink-0" />
            <span className="truncate">
              {locationText}
            </span>
          </div>
          <div className="flex justify-between text-sm text-gray-500 border-t pt-3">
            {bedrooms > 0 && (
              <div className="flex items-center">
                <FaBed className="mr-1" />
                <span>
                  {bedrooms} {bedrooms === 1 ? "bed" : "beds"}
                </span>
              </div>
            )}
            {bathrooms > 0 && (
              <div className="flex items-center">
                <FaBath className="mr-1" />
                <span>
                  {bathrooms} {bathrooms === 1 ? "bath" : "baths"}
                </span>
              </div>
            )}
            {squareFeet > 0 && (
              <div className="flex items-center">
                <SafeIcon icon={FiSquare} className="mr-1" />
                <span>{squareFeet.toLocaleString()} ft²</span>
              </div>
            )}
          </div>
        </div>
      </article>
    </Link>
  );
});

ListingCard.displayName = 'ListingCard';

ListingCard.propTypes = {
  listing: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    title: PropTypes.string,
    price: PropTypes.number,
    // Support nested location object OR string-based location
    location: PropTypes.oneOfType([
      PropTypes.shape({
        city: PropTypes.string,
        state: PropTypes.string,
        address: PropTypes.string,
        coordinates: PropTypes.arrayOf(PropTypes.number),
      }),
      PropTypes.string,
    ]),
    city: PropTypes.string, // Direct city property
    state: PropTypes.string, // Direct state property
    address: PropTypes.string,
    bedrooms: PropTypes.number,
    bathrooms: PropTypes.number,
    squareFeet: PropTypes.number,
    images: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string.isRequired,
    })),
    status: PropTypes.oneOf(Object.keys(STATUS_TEXT)),
    description: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    propertyType: PropTypes.string,
  }).isRequired,
  onClick: PropTypes.func,
  preloadImages: PropTypes.bool,
  size: PropTypes.oneOf(['small', 'default', 'large']),
};

ListingCard.defaultProps = {
  onClick: undefined,
  preloadImages: false,
  size: 'default',
};

export default ListingCard;
